import { yupResolver } from '@hookform/resolvers/yup';
import { LoadingButton } from '@mui/lab';
import { Link, Typography } from '@mui/material';
import { Stack } from '@mui/system';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import ReCAPTCHA from 'react-google-recaptcha';
import { Trans } from 'react-i18next';
import * as Yup from 'yup';
import { ControlledInput } from '../components/Form';
import { LayoutPublic } from '../components/Layout';
import { useInternationalization, useInvisibleRecaptcha, usePageTitle } from '../hooks';
import useAuth from '../hooks/useAuth';
import { routes } from '../routes';
import { palette } from '../styles/palette';
import { ApiErrorCodes, ApiException, Authentication, Styles } from '../types';
import { VERSION_NUMBER } from '../constants';
import { env } from '../env';

const style: Styles = {
  button: {
    width: '100%',
  },
  forgotPassword: {
    fontSize: '16px',
    fontWeight: 600,
    color: palette.primary.deep,
  },
  termsAndConditions: {
    fontSize: '14px',
  },
  termsAndConditionsLink: {
    fontWeight: 600,
    color: palette.primary.deep,
  },
  disclaimer: {
    maxWidth: {
      xs: '100%',
      md: '65%',
      xl: '45%',
    },
    p: 3,
    textAlign: 'center',
    color: palette.grey[500],
  },
  outageNotice: {
    border: `2px solid ${palette.error.main}`,
    borderRadius: 1,
    backgroundColor: '#FDECEF',
    color: palette.error.main,
    p: 2,
  },
  outageNoticeTitle: {
    fontWeight: 700,
  },
};

export function Login() {
  const { t } = useInternationalization();
  usePageTitle('auth:login.title');
  const { login } = useAuth();
  const [errorKey, setErrorKey] = useState('');
  const { recaptchaRef, executeRecaptcha, resetRecaptcha } = useInvisibleRecaptcha();

  const validationLogin = Yup.object({
    username: Yup.string().required(t('common:error.fieldRequired', { name: t('auth:login.username') })),
    password: Yup.string().required(t('common:error.fieldRequired', { name: t('auth:login.password') })),
  });

  const { handleSubmit, control, formState, resetField } = useForm<Authentication>({
    defaultValues: { username: '', password: '' },
    resolver: yupResolver(validationLogin),
  });

  const onLoginClick = async (auth: Authentication) => {
    try {
      setErrorKey('');

      const captchaToken = await executeRecaptcha();
      if (!captchaToken) {
        setErrorKey('auth:login.captchaRequired');
        return;
      }

      const requestData = { ...auth, captchaToken };
      await login(requestData);
    } catch (error) {
      const errorCode = (error as ApiException).code;
      if (errorCode === ApiErrorCodes.USER_CREDENTIALS_CAPTCHA_ERROR) {
        setErrorKey('auth:login.captchaResponseError');
      } else if (errorCode === ApiErrorCodes.MEMBERS_INACTIVE_OR_ARCHIVED) {
        setErrorKey('auth:login.membersInactiveOrArchived');
      } else if (errorCode === ApiErrorCodes.USER_UNVERIFIED) {
        setErrorKey('auth:login.unverified');
      } else {
        setErrorKey('auth:login.invalid');
      }
      resetField('password');
    } finally {
      resetRecaptcha();
    }
  };
  const footer = (
    <>
      <Typography>{t('common:version')} {VERSION_NUMBER}</Typography>
      <Typography sx={style.disclaimer}>{t('auth:disclaimer')}</Typography>
    </>
  );
  return (
    <LayoutPublic
      title={t('auth:login.title')}
      footer={footer}
    >
      <form id="login-form" onSubmit={handleSubmit(onLoginClick)}>
        <Stack spacing={2}>
          <Stack spacing={2} sx={style.outageNotice} role="alert">
            <Typography>
              <Typography component="span" sx={style.outageNoticeTitle}>
                Service Outage:
              </Typography>{' '}
              The service is currently unavailable. We are aware of the situation and are doing everything we can to
              restore it in a timely manner.
            </Typography>
            <Typography>
              <Typography component="span" sx={style.outageNoticeTitle}>
                Panne de service :
              </Typography>{' '}
              Le service est actuellement indisponible. Nous sommes au courant de la situation et faisons tout notre
              possible pour le rétablir dans les meilleurs délais.
            </Typography>
          </Stack>
          <ControlledInput label={t('auth:login.username')} name="username" control={control} />
          <ControlledInput label={t('auth:login.password')} type="password" name="password" control={control} />
          <Link href={routes.ForgotPassword.path} sx={style.forgotPassword}>
            {t('auth:login.forgotPassword')}
          </Link>
          <Typography variant="body1" sx={style.termsAndConditions}>
            <Trans i18nKey="auth:login.acceptTermsAndConditions">
              <Link href={routes.TermsAndConditions.path} sx={style.termsAndConditionsLink} />
            </Trans>
          </Typography>
          <ReCAPTCHA ref={recaptchaRef} sitekey={env.REACT_APP_RECAPTCHA_SITE_KEY} size="invisible" />
          <LoadingButton sx={style.button} loading={formState.isSubmitting} type="submit" variant="contained">
            {t('auth:login.action')}
          </LoadingButton>
          {!!errorKey && <Typography variant="inputError">{t(errorKey)}</Typography>}
        </Stack>
      </form>
    </LayoutPublic>
  );
}
